import { MessageState } from "@/gen/bmi/v1/batch_message_pb";
import {
  MESSAGE_STATE_ORDER,
  formatCount,
  formatPercent,
  messageStateFromProtoName,
  messageStateLabel,
  messageStateTone,
  toneSolidClass,
  toneTextClass,
} from "@/lib/format";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

export interface StateSlice {
  state: MessageState;
  count: number;
}

/**
 * Turns a `MessageState value name -> count` map (as carried on Batch and
 * GetStatsResponse) into ordered slices, dropping zeroes.
 */
export function sliceStateCounts(counts: Record<string, number>): StateSlice[] {
  const byState = new Map<MessageState, number>();
  for (const [name, count] of Object.entries(counts)) {
    if (count <= 0) continue;
    const state = messageStateFromProtoName(name) ?? MessageState.UNSPECIFIED;
    byState.set(state, (byState.get(state) ?? 0) + count);
  }
  const ordered: StateSlice[] = [];
  for (const state of MESSAGE_STATE_ORDER) {
    const count = byState.get(state);
    if (count) ordered.push({ state, count });
    byState.delete(state);
  }
  for (const [state, count] of byState) ordered.push({ state, count });
  return ordered;
}

export function StateCountBar({
  counts,
  total,
  className,
}: {
  counts: Record<string, number>;
  total: number;
  className?: string;
}) {
  const slices = sliceStateCounts(counts);
  const sum = slices.reduce((acc, s) => acc + s.count, 0) || 1;

  return (
    <div className={cn("flex h-2 w-full overflow-hidden rounded-full bg-muted", className)}>
      {slices.map((slice) => (
        <Tooltip key={slice.state}>
          <TooltipTrigger asChild>
            <div
              className={cn("h-full first:rounded-l-full last:rounded-r-full", toneSolidClass(messageStateTone(slice.state)))}
              style={{ width: `${(slice.count / sum) * 100}%` }}
            />
          </TooltipTrigger>
          <TooltipContent>
            {messageStateLabel(slice.state)}: {formatCount(slice.count)} (
            {formatPercent(slice.count, total || sum)})
          </TooltipContent>
        </Tooltip>
      ))}
    </div>
  );
}

export function StateCountLegend({
  counts,
  className,
}: {
  counts: Record<string, number>;
  className?: string;
}) {
  const slices = sliceStateCounts(counts);
  if (slices.length === 0) {
    return <p className={cn("text-xs text-muted-foreground", className)}>No messages yet.</p>;
  }
  return (
    <ul className={cn("flex flex-wrap gap-x-4 gap-y-1 text-xs", className)}>
      {slices.map((slice) => {
        const tone = messageStateTone(slice.state);
        return (
          <li key={slice.state} className="flex items-center gap-1.5">
            <span className={cn("size-2 rounded-full", toneSolidClass(tone))} />
            <span className="text-muted-foreground">{messageStateLabel(slice.state)}</span>
            <span className={cn("tabular font-medium", toneTextClass(tone))}>
              {formatCount(slice.count)}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
